import {
  Alignment,
  Button,
  Classes,
  Collapse,
  Colors,
  Icon,
  Menu,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ContextMenu2 } from "@blueprintjs/popover2";
import classnames from "classnames";
import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { MenuItem as BpMenuItem } from "@blueprintjs/core";
import { useNavigationContext } from "../../contexts";
import { useCommonHooks, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { CustomerColors } from "../../theme";
import { CollapseAllContext } from "./CollapseAllContext";
import { Container } from "./Container";
import { INavigationItem } from "./INavigationItem";

const ButtonStyled = styled(Button)`
  /* Change colors for customer */
  margin: 0em;
  &.level_0 {
    background-color: ${CustomerColors.green} !important;
    border-radius: 0;
    padding: 0.5em !important;
    padding-left: 15px !important;
    &:hover {
      background-color: ${CustomerColors.green} !important;
    }
  }
  &.level_1.has-children {
    /* border: 1px solid white; */
    margin-top: 1px;
    margin-bottom: 1px;
    background-color: ${Colors.BLUE3} !important;
    border-radius: 0;
    &:hover {
      background-color: ${CustomerColors.green} !important;
    }
  }
  &.level_1 {
    padding-left: 25px !important;
  }
  &.is-active {
    font-weight: bold;
    background-color: ${Colors.GRAY1} !important;
    text-decoration: blink !important;
  }
`;

const LinkStyled = styled(Link)`
  /* Change colors for customer */
  margin: 0em;
  &.level_0 {
    background-color: ${CustomerColors.green} !important;
    border-radius: 0;
    padding: 0.5em !important;
    padding-left: 15px !important;
    &:hover {
      background-color: ${CustomerColors.green} !important;
    }
  }
  &.level_1.has-children {
    /* border: 1px solid white; */
    margin-top: 1px;
    margin-bottom: 1px;
    background-color: ${Colors.BLUE3} !important;
    border-radius: 0;
    &:hover {
      background-color: ${CustomerColors.green} !important;
    }
  }
  &.level_1 {
    padding-left: 25px !important;
  }
  &.is-active {
    font-weight: bold;
    background-color: ${Colors.GRAY1} !important;
    text-decoration: blink !important;
  }
`;

export interface IMenuItemProps {
  item: INavigationItem;
  level: number;
  disabled?: boolean;
  maxAutoCollapseLevel?: number;
  onNavigation?: () => void;
  rightElement?: React.ReactElement;
}

const CenteredIcon = styled(Icon)`
  align-self: center;
`;

export const MenuItem: React.FunctionComponent<IMenuItemProps> = ({
  item,
  level,
  disabled,
  maxAutoCollapseLevel = 999,
  onNavigation,
  rightElement,
}) => {
  const { hasAction, router } = useCommonHooks();
  const location = useLocation();
  const isActive = React.useMemo(() => {
    return item.route && "/" + location.pathname.split("/")[1] === item.route;
  }, [item, location]);

  const [isExpanded, setIsExpanded] = React.useState(!!item.defaultOpened);
  const { t } = useTl();
  const id = React.useMemo(() => uuidv4(), []);
  const { openedMenuId, setMenuOpenedId } =
    React.useContext(CollapseAllContext);

  const { clearBackRoute } = useNavigationContext();

  React.useEffect(() => {
    const hasItemWithCurrentRoute = (itm: INavigationItem) => {
      if (itm.route && location.pathname.includes(itm.route)) {
        return true;
      }
      if (itm.items) {
        return itm.items.filter((i) => hasItemWithCurrentRoute(i)).length > 0;
      }
      return false;
    };

    if (hasItemWithCurrentRoute(item)) {
      setIsExpanded(true);
      setMenuOpenedId({ level, id });
    }
  }, [id, item, level, location.pathname, setMenuOpenedId]);

  React.useEffect(() => {
    if (
      openedMenuId?.level === level &&
      openedMenuId?.id !== id &&
      level <= maxAutoCollapseLevel
    ) {
      setIsExpanded(false);
    }
  }, [id, level, maxAutoCollapseLevel, openedMenuId]);

  const hasChildren = React.useMemo(() => {
    return item.items && item.items.length > 0;
  }, [item]);

  const collapseIcon = React.useMemo(() => {
    if (hasChildren) {
      return isExpanded ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_DOWN;
    } else {
      return IconNames.BLANK;
    }
  }, [hasChildren, isExpanded]);

  const onClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (hasChildren) {
        if (!isExpanded) {
          setMenuOpenedId({ level, id });
        }
        setIsExpanded((e) => !e);
      } else if (item.route) {
        clearBackRoute();
        if (!event.ctrlKey) {
          router.push(item.route);
        } else {
          window.open(`#${item.route}`);
        }

        if (onNavigation) {
          onNavigation();
        }
      }
    },
    [
      hasChildren,
      item.route,
      isExpanded,
      setMenuOpenedId,
      level,
      id,
      clearBackRoute,
      onNavigation,
      router,
    ]
  );

  return hasAction(item.actions) ? (
    <>
      {item.startGroupTitle && (
        <div style={{ color: "red" }}>{item.startGroupTitle}</div>
      )}
      <>
        {hasChildren ? (
          <ButtonStyled
            text={t(item.name)}
            icon={
              item.alternativeIcon ? (
                <i className={`${Classes.ICON} ${item.alternativeIcon}`}></i>
              ) : (
                item.icon
              )
            }
            rightIcon={
              <div style={{ display: "flex" }}>
                {rightElement}
                <CenteredIcon icon={collapseIcon} />
              </div>
            }
            onClick={onClick}
            style={{ paddingLeft: `${level * 1 + 0.1}em` }}
            minimal={true}
            className={classnames([
              `level_${level}`,
              { "has-children": hasChildren, "is-active": isActive },
            ])}
            alignText={Alignment.LEFT}
            small={false}
            disabled={disabled}
          />
        ) : (
          <div>
            <ContextMenu2
              style={{ width: "100%" }}
              content={
                <Menu>
                  <BpMenuItem
                    text={t(ETLCodes.OpenInNewTab)}
                    icon="key-tab"
                    onClick={() => window.open(`#${item.route}`)}
                  />
                </Menu>
              }
            >
              <ButtonStyled
                text={t(item.name)}
                icon={
                  item.alternativeIcon ? (
                    <i
                      className={`${Classes.ICON} ${item.alternativeIcon}`}
                    ></i>
                  ) : (
                    item.icon
                  )
                }
                rightIcon={
                  <div style={{ display: "flex" }}>
                    {rightElement}
                    <CenteredIcon icon={collapseIcon} />
                  </div>
                }
                onClick={onClick}
                style={{ paddingLeft: `${level * 1 + 0.1}em`, width: "100%" }}
                minimal={true}
                className={classnames([
                  `level_${level}`,
                  { "has-children": hasChildren, "is-active": isActive },
                ])}
                alignText={Alignment.LEFT}
                small={false}
                disabled={disabled}
              />
            </ContextMenu2>
          </div>
        )}

        <Collapse isOpen={isExpanded}>
          <Container>
            {item.items &&
              item.items!.map((itm, index) => (
                <MenuItem
                  key={index}
                  item={itm}
                  level={level + 1}
                  disabled={itm.disabled}
                  maxAutoCollapseLevel={maxAutoCollapseLevel}
                  onNavigation={onNavigation}
                  rightElement={itm?.rightElement}
                />
              ))}
          </Container>
        </Collapse>
      </>
    </>
  ) : null;
};
